import { maybe, rd, RemoteData } from '@passionware/monads';
import { Capability, IntegrationID } from '__types__';
import type { ResourceType } from '__types__/v1/models/ResourceType';
import useModal from 'hooks/useModal';
import AuthService from 'lib/auth/AuthService';
import Env from 'lib/env/Env';
import {
  BarChart3,
  BookOpenCheck,
  Building2,
  Calendar,
  Camera,
  FileText,
  GalleryHorizontal,
  Home,
  List,
  Megaphone,
  Plus,
  PlusIcon,
  Search,
  Stamp,
  Users
} from 'lucide-react';
import { SplashlightDocuSignModal } from 'modals';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { selectActiveAccount } from 'store/accounts';
import { selectIntegrationById } from 'store/integrations';
import type { RootStore } from 'store/reducer';
import {
  selectBrandImage,
  getPublicCompanyName
} from 'store/v1/auth/auth.selectors';
import { getBookingTypes } from 'store/v1/bookings/bookings.selectors';
import { openModal as dispatchOpenModal } from 'store/v1/ui/ui.actions';
import { ResourceTypeIcon } from 'v5/patterns/resource/ResourceTypeIcon';
import { withinApp } from 'v4/core/appRoutes';
import {
  useCurrentResourceId,
  useCurrentResourceTypeId
} from 'v4/entities/common/hooks/useCurrentResourceId';
import { useRouteMatch } from 'react-router-dom';
import { myRouting } from 'v5/features/core/routing';
import { SandboxInfoPopover } from 'v5/features/core/sandbox-info/SandboxInfoPopover';
import { KeyboardShortcut } from 'v5/patterns/widgets/KeyboardShortcut';
import { Button } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { Kbd } from 'v5/design-system/Kbd';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from 'v5/design-system/Tooltip';
import { useManageEnterpriseFeature } from 'v5/features/core/enterprise/useManageEnterpriseFeature';
import { AppScopeProvider } from 'v5/core/app-scope/_private/AppScopeProvider';
import {
  appScopeUtils,
  type ResolvedAppScope
} from 'v5/core/app-scope/appScope.types';
import { appRouting } from 'v5/core/routing/appRouting';
import { teamIdFromScope } from 'v5/api/_common/query/team-scoping';
import { getProductionTypesList } from 'store/v1/production_types/production_types.selectors';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import { EnterpriseMainSidebarHeaderSkeleton } from 'v5/features/core/enterprise/EnterpriseMainSidebarHeaderSkeleton';
import { EnterpriseMenuCollapsibleSkeleton } from 'v5/features/core/enterprise/EnterpriseMenuCollapsibleSkeleton';
import { EnterpriseTeamsNavSection } from 'v5/features/core/enterprise/EnterpriseTeamsNavSection';
import { resolveActiveEnterpriseWorkspace } from 'v5/features/core/enterprise/resolveActiveEnterpriseWorkspace';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { EnterpriseWorkspaceSwitcher } from 'v5/features/core/enterprise/EnterpriseWorkspaceSwitcher';
import { DockableSidebarToggle } from 'v5/patterns/sidebar/DockableLayout';
import { MenuCollapsible, MenuItem } from 'v5/patterns/sidebar/Menu';
import {
  Sidebar,
  SidebarCollapse,
  SidebarContent,
  SidebarGroup,
  SidebarHeader
} from 'v5/patterns/sidebar/Sidebar';
import { SidebarHelpFooter } from 'v5/patterns/sidebar/SidebarHelpFooter';
import { cn } from 'v5/platform/dom/cn';
import { ifCan } from 'v5/platform/react/ifCan';
import { NavLink } from 'v5/platform/react-router';
import { WithServices } from 'v5/platform/ts/services';
import { WithAppScopeService } from 'v5/services/internal/AppScopeService/AppScopeService';
import { WithAccessService } from 'v5/services/front/AccessService/AccessService';
import { WithProductionService } from 'v5/services/io/ProductionService/ProductionService';
import { isAccessProbePending } from 'v5/services/front/AccessService/isAccessProbePending';
import { WithDevOriginService } from 'v5/services/front/DevOriginService/DevOriginService';
import { WithFeatureService } from 'v5/services/front/FeatureService/FeatureService';
import { WithRoutingService } from 'v5/services/front/RoutingService/RoutingService';
import { WithOnlineStatusService } from 'v5/services/front/OnlineStatusService/OnlineStatusService';
import { WithSupportService } from 'v5/services/front/SupportService/SupportService';
import {
  WithContentThemeService,
  WithThemeService
} from 'v5/services/front/ThemeService/ThemeService';
import { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithUserFlowService } from 'v5/services/front/UserFlowService/UserFlowService';
import { WithEnterpriseService } from 'v5/services/io/EnterpriseService/EnterpriseService';
import { WithResourceTypeService } from 'v5/services/internal/ResourceTypeService/ResourceTypeService';
import type { WithGlobalSearchService } from 'v5/services/GlobalSearchService/GlobalSearchService';
import { dashboardPath } from 'v5/core/routing/dashboardRouting';
import { useResource } from '../productions/booking-costs/booking/useBookingEntity';
export interface EnterpriseMainSidebarProps
  extends WithServices<
    [
      WithRoutingService,
      WithFeatureService,
      WithGlobalSearchService,
      WithSupportService,
      WithThemeService,
      WithContentThemeService,
      WithOnlineStatusService,
      WithDevOriginService,
      WithUserPreferencesService,
      WithEnterpriseService,
      WithUserFlowService,
      WithAppScopeService,
      WithResourceTypeService,
      WithAccessService,
      WithProductionService
    ]
  > {
  className?: string;
}

// Local component for menu items with create action button
interface MenuItemWithCreateButtonProps {
  to: string;
  icon?: React.ReactNode;
  level?: number;
  onCreateClick?: (e: React.MouseEvent) => void;
  createTooltip: string;
  children: React.ReactNode;
  className?: string;
  isActiveOverride?: boolean;
  createButtonWrapper?: (node: React.ReactNode) => React.ReactNode;
  autoSelect?: boolean;
}

const MenuItemWithCreateButton: React.FC<MenuItemWithCreateButtonProps> = ({
  to,
  icon,
  level = 0,
  onCreateClick,
  createTooltip,
  children,
  className,
  isActiveOverride,
  createButtonWrapper,
  autoSelect
}) => {
  const createButton = (
    <div
      role="button"
      aria-label={createTooltip}
      className={cn(
        'absolute right-1.5 top-1.5 size-7 flex items-center justify-center group-hover/menu-item:opacity-100 opacity-0 transition-all',
        'rounded bg-transparent! backdrop-blur-3xl hover:bg-bg-emphasis/5!'
      )}
      onClick={onCreateClick}
    >
      <PlusIcon className="size-4 shrink-0" />
    </div>
  );

  const createButtonWithTooltip = createButtonWrapper
    ? createButtonWrapper(createButton)
    : createButton;

  return (
    <NavLink to={to} isActiveOverride={isActiveOverride}>
      <MenuItem
        className={cn('group/menu-item relative', className)}
        level={level}
        icon={icon}
        autoSelect={autoSelect}
      >
        {onCreateClick ? (
          <Tooltip>
            <TooltipTrigger asChild>
              {/* empty div solves problem with dynamic create button not always passing refs and props correctly to the trigger */}
              <div className="">{createButtonWithTooltip}</div>
            </TooltipTrigger>
            <TooltipContent side="right">{createTooltip}</TooltipContent>
          </Tooltip>
        ) : null}
        {children}
      </MenuItem>
    </NavLink>
  );
};

interface ResourceTypeMenuItemProps {
  resourceType: ResourceType;
  href: string;
  onCreateClick?: (resourceTypeId: number) => void;
  activeContactId: number | null;
}

const ResourceTypeMenuItem: React.FC<ResourceTypeMenuItemProps> = ({
  resourceType,
  href,
  onCreateClick,
  activeContactId
}) => {
  const contactResourceTypeId = useResource(activeContactId)?.resource_type_id;
  const activeResourceTypeId = useCurrentResourceTypeId();

  const isActiveFromContact = maybe.mapOrElse(
    contactResourceTypeId,
    resourceTypeId => resourceTypeId === resourceType.id,
    false
  );
  const isActiveFromRoute = activeResourceTypeId === resourceType.id;

  const resourceName = resourceType.name ?? 'Resource';
  const createTooltip = `Create new ${resourceName.toLowerCase()}`;
  const handleCreateClick = React.useCallback(() => {
    onCreateClick?.(resourceType.id);
  }, [onCreateClick, resourceType.id]);

  return (
    <MenuItemWithCreateButton
      level={1}
      to={href}
      icon={
        <ResourceTypeIcon
          icon={resourceType.icon}
          alt={resourceName}
          className="size-4 opacity-80"
        />
      }
      onCreateClick={onCreateClick ? handleCreateClick : undefined}
      createTooltip={createTooltip}
      isActiveOverride={
        isActiveFromContact || isActiveFromRoute ? true : undefined
      }
    >
      {resourceName}
    </MenuItemWithCreateButton>
  );
};

interface BookingTypeMenuItemProps {
  bookingType: { id: number; name: string };
  href: string;
  typeMatchPath: string;
  onCreateClick?: () => void;
}

const BookingTypeMenuItem: React.FC<BookingTypeMenuItemProps> = ({
  bookingType,
  href,
  typeMatchPath,
  onCreateClick
}) => {
  const bookingTypeName = bookingType.name || 'Booking';
  const createTooltip = `Create new ${bookingTypeName.toLowerCase()}`;

  const isOnBookingTypePage = useRouteMatch({
    path: typeMatchPath,
    exact: true
  });

  return (
    <MenuItemWithCreateButton
      level={1}
      to={href}
      icon={<Stamp className="size-4" />}
      onCreateClick={onCreateClick}
      createTooltip={createTooltip}
      isActiveOverride={!!isOnBookingTypePage}
    >
      {bookingTypeName}
    </MenuItemWithCreateButton>
  );
};

type EnterpriseMainSidebarBodyProps = EnterpriseMainSidebarProps & {
  organizationId: string;
  /** Chrome workspace — same slug passed into the wrapping AppScopeProvider. */
  workspaceSlug: string;
  activeWorkspace: EnterpriseWorkspace | undefined;
  workspaceId: number | undefined;
  /** Already smoothed with useLastWithPlaceholder in the outer sidebar. */
  workspacesRd: RemoteData<EnterpriseWorkspace[]>;
  headerReady: boolean;
  canManage: boolean;
};

/**
 * Chrome under a workspace AppScopeProvider (picker / active workspace).
 * Route/page scope stays outside — only this sidebar remaps context.
 */
function EnterpriseMainSidebarBody({
  services,
  className,
  organizationId,
  workspaceSlug,
  activeWorkspace,
  workspaceId,
  workspacesRd,
  headerReady,
  canManage
}: EnterpriseMainSidebarBodyProps) {
  const canRd = services.accessService.useCan(
    services.appScopeService.useResolvedScopeIdentity()
  );
  const accessPending = isAccessProbePending(canRd);
  const { t } = useTranslation('core', { keyPrefix: 'enterprise' });
  const scopeRd = services.appScopeService.useResolvedAppScope();
  const scopeRdStable = rd.useLastWithPlaceholder(scopeRd);
  const scope = rd.tryGet(scopeRdStable);
  const slugAppScope = services.appScopeService.useSlugAppScope();
  const history = useHistory();
  const orgRouting = appRouting.org(organizationId);

  const workspaceLinks = useMemo(
    () => orgRouting.workspace(workspaceSlug),
    [orgRouting, workspaceSlug]
  );
  const { resources, callouts, bookings, calendar } = workspaceLinks;

  const teamsRd = services.enterpriseService.useTeams(
    workspaceId != null
      ? maybe.of({
          organizationId,
          workspaceId
        })
      : maybe.ofAbsent()
  );
  const companyName = useSelector(getPublicCompanyName);
  const brandImage = useSelector(selectBrandImage);
  const account = useSelector(selectActiveAccount);
  const resourceTypesRd = services.resourceTypeService.useResourceTypes(
    rd.tryMap(scopeRdStable, appScopeUtils.identityFromResolved)
  );

  const resourceTypes = rd.tryGet(resourceTypesRd) ?? [];
  const bookingTypes = useSelector(getBookingTypes);
  const productionTypes = useSelector(getProductionTypesList);
  const dispatch = useDispatch();
  const { openModal: openV2Modal } = useModal();

  /**
   * Switching workspace lands on its dashboard, not on its resource list: the
   * question you arrive with is "what is happening here", and the list of every
   * resource answers none of it. `/app` and `/app/:id` already resolve this way
   * (PrivateRoute), so this only brings the enterprise chrome in line.
   */
  const handleWorkspaceSelect = (workspaceSlug: string) => {
    history.push(orgRouting.workspace(workspaceSlug).dashboard());
  };

  const handleCreateWorkspace = () => {
    services.userFlowService.openCreateEnterpriseWorkspaceDialog(
      organizationId,
      {
        appScope: slugAppScope,
        onCreated: workspace => {
          handleWorkspaceSelect(workspace.slug);
        }
      }
    );
  };

  const handleCreateTeam = () => {
    if (workspaceId == null) {
      return;
    }
    services.userFlowService.openCreateEnterpriseTeamDialog({
      organizationId,
      workspaceId,
      appScope: slugAppScope
    });
  };

  const activeContactId = useCurrentResourceId();
  const activeResourceId = useCurrentResourceId();
  const isContactRoute = activeContactId != null;
  const isResourceRoute = activeResourceId != null;
  // Org-talents list + unscoped contact detail; workspace contact detail keeps Resources.
  const onOrganizationTalentsList = useRouteMatch({
    path: appRouting.org().organization.organizationTalents(),
    exact: false
  });
  const onUnscopedContact = useRouteMatch({
    path: myRouting.forWorkspace().contacts().detail(),
    exact: false
  });
  const isOrganizationTalentsNavActive =
    !!onOrganizationTalentsList || !!onUnscopedContact;

  const to = withinApp(organizationId);

  const isOnEnterpriseResourcesRoot = useRouteMatch({
    path: resources.root(),
    exact: true
  });
  const isOnLegacyResourcesRoot = useRouteMatch({
    path: myRouting.forWorkspace(organizationId).resources().root(),
    exact: true
  });
  const isOnResourcesRoot =
    isOnEnterpriseResourcesRoot || isOnLegacyResourcesRoot;

  const isOnBookingsRoot = useRouteMatch({
    path: bookings.root(),
    exact: true
  });

  // Get keyboard shortcuts from preferences service
  const shortcuts = services.userPreferencesService.useShortcuts();

  const docusignIntegration = useSelector((state: RootStore) =>
    selectIntegrationById(state, IntegrationID.DOCUSIGN)
  );

  // Check if we're in sandbox/QA environment
  const isSandbox = !Env.isProd || Env.isQAEnvironment;

  const handleLogout = () => {
    AuthService.logout();
  };

  const handleOpenSearch = () => {
    services.globalSearchService.openSearchEvent.emit();
  };

  const handleCreateResource = (resourceTypeId: number) => {
    services.userFlowService.openCreateResourceModal({
      resourceTypeId,
      ...(workspaceId != null ? { enterpriseWorkspaceId: workspaceId } : {}),
      appScope: slugAppScope
    });
  };

  const handleCreateProduction = (
    productionTypeId?: number,
    scopeOverride?: {
      enterpriseWorkspaceId?: number;
      enterpriseTeamId?: number;
    }
  ) => {
    const enterpriseWorkspaceId =
      scopeOverride?.enterpriseWorkspaceId ?? workspaceId;
    const enterpriseTeamId =
      scopeOverride?.enterpriseTeamId ?? teamIdFromScope(scope);
    const teamsForRedirect = rd.tryGet(teamsRd);
    dispatch(
      dispatchOpenModal(
        'ProductionSetUpModal',
        {
          create: true,
          productionTypeId,
          production: {
            ...(enterpriseWorkspaceId != null
              ? { enterprise_workspace_id: enterpriseWorkspaceId }
              : {}),
            ...(enterpriseTeamId != null
              ? { enterprise_team_id: enterpriseTeamId }
              : {})
          },
          workspaces: rd.tryGet(workspacesRd),
          teams: teamsForRedirect
        },
        slugAppScope
      )
    );
  };

  const handleCreateShortlist = () => {
    services.userFlowService.openCreateShortlistModal({
      enterpriseWorkspaceId: workspaceId,
      enterpriseTeamId: teamIdFromScope(scope),
      appScope: slugAppScope
    });
  };

  const handleCreateBooking = () => {
    services.userFlowService.openCreateBookingModal({
      appScope: slugAppScope
    });
  };

  const handleCreateCallsheet = () => {
    services.userFlowService.openCreateCallsheetDialog(organizationId, {
      appScope: slugAppScope
    });
  };

  const handleCreateCallout = () => {
    void services.userFlowService.openCreateCalloutModal({
      enterpriseWorkspaceId: workspaceId,
      appScope: slugAppScope
    });
  };

  const handleCreateDocuSignRequest = () => {
    openV2Modal(() => <SplashlightDocuSignModal />);
  };

  function renderShortcut(shortcut: RemoteData<string>) {
    return rd.tryMap(shortcut, shortcut => (
      <Kbd className="ml-auto">
        <KeyboardShortcut shortcut={shortcut} />
      </Kbd>
    ));
  }

  const manageEnterpriseLink = canManage ? (
    <NavLink to={orgRouting.settings.global.manageEnterprise()}>
      <MenuItem icon={<Building2 className="size-4" />}>
        {t('manageEnterprise')}
      </MenuItem>
    </NavLink>
  ) : null;

  return (
    <Sidebar className={cn('group/sidebar', className)}>
      <SidebarHeader className="flex flex-col gap-2 min-h-(--header-height)">
        {headerReady ? (
          <>
            <div className="flex items-center justify-between ml-3">
              <div className="flex items-center gap-2 flex-1">
                <EnterpriseWorkspaceIcon
                  name={activeWorkspace?.name ?? 'Workspace'}
                  iconUrl={activeWorkspace?.iconUrl ?? ''}
                  className="w-8 h-8 rounded dark:border dark:border-white/20"
                />
                {isSandbox && <SandboxInfoPopover />}
              </div>
              <DockableSidebarToggle>
                <SidebarCollapse className="group-hocus/sidebar:opacity-100 opacity-0 transition-opacity" />
              </DockableSidebarToggle>
            </div>

            <EnterpriseWorkspaceSwitcher
              workspacesRd={workspacesRd}
              activeWorkspaceId={workspaceSlug}
              activeWorkspaceName={activeWorkspace?.name ?? 'Workspace'}
              activeWorkspaceIconUrl={activeWorkspace?.iconUrl ?? ''}
              organizationName={companyName ?? 'Organization'}
              organizationIconUrl={brandImage ?? ''}
              accountDisplayName={account?.displayName}
              accountProfileImageUrl={account?.profileImage}
              getWorkspacePath={workspaceSlug =>
                orgRouting.workspace(workspaceSlug).dashboard()
              }
              workspaceSettingsTo={orgRouting.settings
                .workspace(workspaceSlug)
                .general()}
              organizationSettingsTo={orgRouting.settings.global.manageEnterprise()}
              accountDetailsTo={orgRouting.settings.personal.account()}
              onLogout={handleLogout}
              canManage={canManage}
              onCreateWorkspace={handleCreateWorkspace}
              enterpriseManagementTo={
                canManage
                  ? orgRouting.settings.global.manageEnterprise()
                  : undefined
              }
            />
          </>
        ) : (
          <EnterpriseMainSidebarHeaderSkeleton includeActions={false} />
        )}

        <div className="flex flex-row gap-2">
          <Button
            variant="secondary"
            size="sm"
            className="flex-1 gap-4 justify-start relative pr-1.5"
            onClick={handleOpenSearch}
          >
            <Search className="size-4 ml-1" />
            <span>Search</span>
            <Kbd className="ml-auto font-normal self-stretch rounded bg-bg-subtle-2">
              <KeyboardShortcut shortcut="mod+k" />
            </Kbd>
          </Button>

          {ifCan
            .ready(canRd)
            .render(can => {
              const resourcesGroup = ifCan(can)
                .render(can =>
                  (resourceTypes?.length ?? 0) !== 0 &&
                  can({ action: Capability.RESOURCE_CREATE }) ? (
                    <>
                      <DropdownMenuLabel>New resource</DropdownMenuLabel>
                      <DropdownMenuGroup>
                        {resourceTypes.map(resourceType => (
                          <DropdownMenuItem
                            key={resourceType.id}
                            onClick={() =>
                              handleCreateResource(resourceType.id)
                            }
                          >
                            <ResourceTypeIcon
                              icon={resourceType.icon}
                              alt={resourceType.name}
                              className="size-4 opacity-80"
                            />
                            <span className="truncate">
                              {resourceType.name}
                            </span>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuGroup>
                    </>
                  ) : null
                )
                .otherwise(null);

              const entitiesGroup = ifCan(can)
                .render(can => (
                  <>
                    <DropdownMenuLabel>New</DropdownMenuLabel>
                    <DropdownMenuGroup>
                      {can({ action: Capability.PRODUCTION_CREATE }) && (
                        <DropdownMenuItem
                          onClick={() => handleCreateProduction()}
                        >
                          <Camera className="size-4" />
                          Production
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.production)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can({ action: Capability.SHORTLIST_CREATE }) && (
                        <DropdownMenuItem onClick={handleCreateShortlist}>
                          <GalleryHorizontal className="size-4" />
                          Shortlist
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.shortlist)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can({ action: Capability.BOOKING_CREATE }) && (
                        <DropdownMenuItem onClick={handleCreateBooking}>
                          <Stamp className="size-4" />
                          Booking
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.booking)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can({ action: Capability.CALLSHEET_CREATE }) && (
                        <DropdownMenuItem onClick={handleCreateCallsheet}>
                          <FileText className="size-4" />
                          Callsheet
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.callsheet)
                          )}
                        </DropdownMenuItem>
                      )}
                      {can({ action: Capability.CALLOUT_CREATE }) && (
                        <DropdownMenuItem onClick={handleCreateCallout}>
                          <Megaphone className="size-4" />
                          Callout
                          {renderShortcut(
                            rd.map(shortcuts, s => s.create.callout)
                          )}
                        </DropdownMenuItem>
                      )}
                      {docusignIntegration?.enabled &&
                        can({ action: Capability.DOCUSIGN_SEND }) && (
                          <DropdownMenuItem
                            onClick={handleCreateDocuSignRequest}
                          >
                            <FileText className="size-4" />
                            Send DocuSign Request
                          </DropdownMenuItem>
                        )}
                    </DropdownMenuGroup>
                  </>
                ))
                .otherwise(null);

              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="secondary" size="sm">
                      <Plus className="size-4" />
                      Create
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    side="right"
                    className="max-w-[250px]"
                  >
                    {resourcesGroup}
                    {resourcesGroup && entitiesGroup ? (
                      <DropdownMenuSeparator />
                    ) : null}
                    {entitiesGroup}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })
            .otherwise(null)}
        </div>

        <NavLink
          to={
            workspaceSlug
              ? dashboardPath(organizationId, workspaceSlug)
              : orgRouting.organization.dashboard()
          }
        >
          <MenuItem icon={<Home className="size-4" />}>Dashboard</MenuItem>
        </NavLink>
      </SidebarHeader>

      <SidebarContent className="px-2 flex flex-col gap-2">
        {accessPending ? (
          <SidebarGroup label="Workspace">
            <EnterpriseMenuCollapsibleSkeleton count={4} />
          </SidebarGroup>
        ) : (
          ifCan
            .ready(canRd)
            .render(can => {
              const canCreateResource = can({
                action: Capability.RESOURCE_CREATE
              });
              const canCreateBooking = can({
                action: Capability.BOOKING_CREATE
              });
              const canCreateCallout = can({
                action: Capability.CALLOUT_CREATE
              });

              return (
                <SidebarGroup label="Workspace">
                  {can({ action: Capability.RESOURCE_READ }) && (
                    <>
                      <MenuCollapsible
                        icon={<Users className="size-4" />}
                        label="Resources"
                        expandOnChildSelect
                        defaultOpen={
                          (!isOrganizationTalentsNavActive && isContactRoute) ||
                          isResourceRoute
                        }
                      >
                        <MenuItemWithCreateButton
                          level={1}
                          to={resources.root()}
                          icon={<List className="size-4" />}
                          onCreateClick={
                            canCreateResource
                              ? () =>
                                  handleCreateResource(
                                    resourceTypes?.[0]?.id || 1
                                  )
                              : undefined
                          }
                          createTooltip="Create new resource"
                          isActiveOverride={!!isOnResourcesRoot}
                          autoSelect
                        >
                          All
                        </MenuItemWithCreateButton>
                        {resourceTypes?.map(resourceType => (
                          <ResourceTypeMenuItem
                            key={resourceType.id}
                            resourceType={resourceType}
                            href={resources.forResourceType(resourceType.id)}
                            onCreateClick={
                              canCreateResource
                                ? handleCreateResource
                                : undefined
                            }
                            activeContactId={
                              isOrganizationTalentsNavActive
                                ? null
                                : activeContactId
                            }
                          />
                        ))}
                      </MenuCollapsible>

                      <NavLink to={calendar.resources.home()}>
                        <MenuItem icon={<Calendar className="size-4" />}>
                          Resource calendar
                        </MenuItem>
                      </NavLink>
                    </>
                  )}

                  {can({ action: Capability.BOOKING_READ }) && (
                    <MenuCollapsible
                      icon={<Stamp className="size-4" />}
                      label="Bookings"
                      expandOnChildSelect
                    >
                      <MenuItemWithCreateButton
                        level={1}
                        to={bookings.root()}
                        icon={<List className="size-4" />}
                        onCreateClick={
                          canCreateBooking ? handleCreateBooking : undefined
                        }
                        createTooltip="Create new booking"
                        isActiveOverride={!!isOnBookingsRoot}
                        autoSelect
                      >
                        All
                      </MenuItemWithCreateButton>
                      {bookingTypes?.map(bookingType => (
                        <BookingTypeMenuItem
                          key={bookingType.id}
                          bookingType={bookingType}
                          href={bookings.type(bookingType.id)}
                          typeMatchPath={bookings.type(bookingType.id)}
                          onCreateClick={
                            canCreateBooking ? handleCreateBooking : undefined
                          }
                        />
                      ))}
                    </MenuCollapsible>
                  )}

                  {can({ action: Capability.CALLOUT_READ }) && (
                    <MenuItemWithCreateButton
                      to={callouts.root()}
                      icon={<Megaphone className="size-4" />}
                      onCreateClick={
                        canCreateCallout ? handleCreateCallout : undefined
                      }
                      createTooltip="Create new callout"
                    >
                      Callouts
                    </MenuItemWithCreateButton>
                  )}

                  {can({ action: Capability.PRODUCTION_READ }) && (
                    <NavLink to={calendar.productions.home()}>
                      <MenuItem icon={<Calendar className="size-4" />}>
                        Production calendar
                      </MenuItem>
                    </NavLink>
                  )}
                </SidebarGroup>
              );
            })
            .otherwise(null)
        )}

        <EnterpriseTeamsNavSection
          services={services}
          teamsRd={teamsRd}
          organizationId={organizationId}
          workspaceSlug={workspaceSlug}
          productionTypes={productionTypes}
          onCreateProduction={handleCreateProduction}
          canManage={canManage}
          onCreateTeam={handleCreateTeam}
        />

        {accessPending ? (
          <SidebarGroup label="Tools">
            <EnterpriseMenuCollapsibleSkeleton count={3} />
          </SidebarGroup>
        ) : (
          ifCan
            .ready(canRd)
            .render(can => (
              <SidebarGroup label="Tools">
                {can({ action: Capability.RESOURCE_READ }) && (
                  <NavLink
                    to={to.organizationTalents.home()}
                    isActiveOverride={
                      isOrganizationTalentsNavActive ? true : undefined
                    }
                  >
                    <MenuItem icon={<Users className="size-4" />}>
                      {t('organizationTalents')}
                    </MenuItem>
                  </NavLink>
                )}
                {manageEnterpriseLink}
                {can({ action: Capability.MANAGE_RECONCILIATION }) && (
                  <NavLink to={to.reconciliations.home()}>
                    <MenuItem icon={<Stamp className="size-4" />}>
                      Reconciliation
                    </MenuItem>
                  </NavLink>
                )}
                {can({ action: Capability.REPORT_DOWNLOAD }) && (
                  <NavLink to={to.reporting.home()}>
                    <MenuItem icon={<BarChart3 className="size-4" />}>
                      Reporting
                    </MenuItem>
                  </NavLink>
                )}
                {can({
                  action: Capability.CONFIGURE_CUSTOM_NIKE_FRONT_DESK
                }) && (
                  <NavLink to={to.frontdesk.home()}>
                    <MenuItem icon={<BookOpenCheck className="size-4" />}>
                      Front desk
                    </MenuItem>
                  </NavLink>
                )}
              </SidebarGroup>
            ))
            .otherwise(
              manageEnterpriseLink ? (
                <SidebarGroup label="Tools">
                  {manageEnterpriseLink}
                </SidebarGroup>
              ) : null
            )
        )}
      </SidebarContent>

      <SidebarHelpFooter services={services} />
    </Sidebar>
  );
}

/**
 * Resolves the active workspace from route scope + workspace list, then mounts
 * chrome under a workspace AppScopeProvider so feature gates match the picker.
 */
export function EnterpriseMainSidebar({
  services,
  className
}: EnterpriseMainSidebarProps) {
  const routeScopeRd = services.appScopeService.useResolvedAppScope();
  const organizationId = maybe.getOrElse(
    services.appScopeService.useMaybeOrganizationId(),
    ''
  );

  const workspacesRd = services.enterpriseService.useWorkspaces(
    organizationId ? maybe.of(organizationId) : maybe.ofAbsent()
  );
  const workspacesRdStable = rd.useLastWithPlaceholder(workspacesRd);
  const headerReady =
    rd.isSuccess(workspacesRdStable) ||
    rd.isPlaceholderData(workspacesRdStable);

  // Org-level pages (organization talents, reporting) name no workspace: keep
  // the one the sidebar already shows instead of jumping to the first. A slug
  // that fails to resolve counts as one, so NotFound keeps a usable sidebar; a
  // refetch of that failed slug keeps it too, instead of flipping to skeleton.
  const [lastWorkspaceSlug, setLastWorkspaceSlug] = useState<string>();
  const organizationScopeRd = useMemo(
    () => rd.of<ResolvedAppScope>({ level: 'organization', organizationId }),
    [organizationId]
  );
  const chromeScopeRd = rd.useLastWithPlaceholder(
    rd.isError(routeScopeRd) ? organizationScopeRd : routeScopeRd
  );
  const activeEnterpriseRd = rd.useMemoMap(
    rd.useCombine({
      workspaces: workspacesRdStable,
      scope: chromeScopeRd
    }),
    ({ workspaces, scope }, fallbackWorkspaceSlug) =>
      resolveActiveEnterpriseWorkspace(
        workspaces,
        scope,
        fallbackWorkspaceSlug
      ),
    lastWorkspaceSlug
  );
  const { activeWorkspace, activeWorkspaceSlug, activeWorkspaceId } =
    rd.getOrElse(activeEnterpriseRd, {
      activeWorkspace: undefined,
      activeWorkspaceSlug: '',
      activeWorkspaceId: undefined,
      browsingWorkspaceScope: {
        level: 'organization' as const,
        organizationId
      }
    });

  useEffect(() => {
    if (activeWorkspaceSlug) {
      setLastWorkspaceSlug(activeWorkspaceSlug);
    }
  }, [activeWorkspaceSlug]);

  const canManage = useManageEnterpriseFeature(
    services.featureService,
    services.appScopeService
  );

  if (!organizationId || !activeWorkspaceSlug) {
    return (
      <Sidebar className={cn('group/sidebar', className)}>
        <SidebarHeader className="flex flex-col gap-2 min-h-(--header-height)">
          <EnterpriseMainSidebarHeaderSkeleton includeActions={false} />
        </SidebarHeader>
        <SidebarContent className="px-2 flex flex-col gap-2">
          <SidebarGroup label="Workspace">
            <EnterpriseMenuCollapsibleSkeleton count={4} />
          </SidebarGroup>
          <SidebarGroup label="Teams">
            <EnterpriseMenuCollapsibleSkeleton count={3} />
          </SidebarGroup>
        </SidebarContent>
        <SidebarHelpFooter services={services} />
      </Sidebar>
    );
  }

  return (
    <AppScopeProvider
      scope={{
        level: 'workspace',
        organizationId,
        workspaceSlug: activeWorkspaceSlug
      }}
    >
      <EnterpriseMainSidebarBody
        services={services}
        className={className}
        organizationId={organizationId}
        workspaceSlug={activeWorkspaceSlug}
        activeWorkspace={activeWorkspace}
        workspaceId={activeWorkspaceId}
        workspacesRd={workspacesRdStable}
        headerReady={headerReady}
        canManage={canManage}
      />
    </AppScopeProvider>
  );
}
